var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { syntheticEvent } from '../components/helpers';
import { bandWidth, linearScale } from './charts/core/scales';
import { getActiveDashboard } from './theme';
/** Map scene world coordinates to chart-group local space (handles nested panels). */
export function chartLocalPoint(group, worldX, worldY) {
    var inv = group.getWorldMatrix().invert();
    if (inv)
        return inv.transformPoint(worldX, worldY);
    return { x: worldX - group.x, y: worldY - group.y };
}
/** Compute evenly spaced tick values for a numeric range. */
export function computeTicks(min, max, count) {
    if (count === void 0) { count = 5; }
    if (count < 2)
        return [min];
    if (min === max)
        return [min, max];
    var step = (max - min) / (count - 1);
    return Array.from({ length: count }, function (_, i) { return Math.round((min + i * step) * 100) / 100; });
}
export function dataBounds(data, minY, maxY) {
    var dataMin = data.length ? Math.min.apply(Math, __spreadArray([], __read(data), false)) : 0;
    var dataMax = data.length ? Math.max.apply(Math, __spreadArray([], __read(data), false)) : 1;
    var min = minY !== null && minY !== void 0 ? minY : Math.min(dataMin, 0);
    var max = maxY !== null && maxY !== void 0 ? maxY : dataMax;
    if (max <= min)
        max = min + 1;
    return { min: min, max: max };
}
export function defaultLayout(width, height, padding, legendHeight) {
    if (padding === void 0) { padding = 30; }
    if (legendHeight === void 0) { legendHeight = 0; }
    var legendH = Math.max(0, legendHeight);
    return {
        plotX: padding,
        plotY: 10,
        plotWidth: Math.max(8, width - padding - 10),
        plotHeight: Math.max(8, height - padding - 10 - legendH),
    };
}
export function seriesToPoints(data, layout, bounds) {
    var step = layout.plotWidth / Math.max(data.length - 1, 1);
    var range = bounds.max - bounds.min || 1;
    var points = [];
    for (var i = 0; i < data.length; i++) {
        points.push(layout.plotX + i * step, layout.plotY + layout.plotHeight - ((data[i] - bounds.min) / range) * layout.plotHeight);
    }
    return points;
}
export function areaPathFromPoints(points, baselineY) {
    if (points.length < 4)
        return '';
    var d = "M ".concat(points[0], " ").concat(points[1]);
    for (var i = 2; i < points.length; i += 2) {
        d += " L ".concat(points[i], " ").concat(points[i + 1]);
    }
    var lastX = points[points.length - 2];
    d += " L ".concat(lastX, " ").concat(baselineY, " L ").concat(points[0], " ").concat(baselineY, " Z");
    return d;
}
export function addGridLines(app, group, layout, yTicks, bounds, xDivisions) {
    var e_1, _a;
    if (xDivisions === void 0) { xDivisions = 4; }
    var range = bounds.max - bounds.min || 1;
    try {
        for (var yTicks_1 = __values(yTicks), yTicks_1_1 = yTicks_1.next(); !yTicks_1_1.done; yTicks_1_1 = yTicks_1.next()) {
            var tick = yTicks_1_1.value;
            var y = layout.plotY + layout.plotHeight - ((tick - bounds.min) / range) * layout.plotHeight;
            group.add(app.line({
                x: layout.plotX,
                y: y,
                x2: layout.plotWidth,
                y2: 0,
                stroke: getActiveDashboard().chartGrid,
                strokeWidth: 1,
                dash: [4, 4],
                listening: false,
            }));
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (yTicks_1_1 && !yTicks_1_1.done && (_a = yTicks_1.return)) _a.call(yTicks_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    for (var i = 0; i <= xDivisions; i++) {
        var x = layout.plotX + (layout.plotWidth / xDivisions) * i;
        group.add(app.line({
            x: x,
            y: layout.plotY,
            x2: 0,
            y2: layout.plotHeight,
            stroke: getActiveDashboard().chartGrid,
            strokeWidth: 1,
            dash: [4, 4],
            listening: false,
        }));
    }
}
export function addAxes(app, group, layout, bounds, yTicks, tickCount) {
    var e_2, _a;
    if (tickCount === void 0) { tickCount = 5; }
    group.add(app.line({
        x: layout.plotX,
        y: layout.plotY,
        x2: 0,
        y2: layout.plotHeight,
        stroke: getActiveDashboard().chartAxis,
        strokeWidth: 1,
        listening: false,
    }));
    group.add(app.line({
        x: layout.plotX,
        y: layout.plotY + layout.plotHeight,
        x2: layout.plotWidth,
        y2: 0,
        stroke: getActiveDashboard().chartAxis,
        strokeWidth: 1,
        listening: false,
    }));
    var range = bounds.max - bounds.min || 1;
    var ticks = yTicks.length ? yTicks : computeTicks(bounds.min, bounds.max, tickCount);
    try {
        for (var ticks_1 = __values(ticks), ticks_1_1 = ticks_1.next(); !ticks_1_1.done; ticks_1_1 = ticks_1.next()) {
            var tick = ticks_1_1.value;
            var y = layout.plotY + layout.plotHeight - ((tick - bounds.min) / range) * layout.plotHeight;
            group.add(app.text({
                text: String(tick),
                x: 2,
                y: y - 6,
                fontSize: getActiveDashboard().fontSizeSm,
                fill: getActiveDashboard().textMuted,
                listening: false,
            }));
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (ticks_1_1 && !ticks_1_1.done && (_a = ticks_1.return)) _a.call(ticks_1);
        }
        finally { if (e_2) throw e_2.error; }
    }
}
export function addLegend(app, group, items, x, y) {
    items.forEach(function (item, i) {
        var ly = y + i * 18;
        group.add(app.rect({ x: x, y: ly, width: 12, height: 12, fill: item.color, listening: false }), app.text({ text: item.label, x: x + 16, y: ly - 1, fontSize: getActiveDashboard().fontSizeSm, fill: getActiveDashboard().text, listening: false }));
    });
}
export function nearestDataIndex(data, layout, localX) {
    return nearestPlotIndex(data.length, layout, localX);
}
/** Index along plot X for a given point count (multi-series charts). */
export function nearestPlotIndex(count, layout, localX) {
    if (count <= 0)
        return 0;
    var step = layout.plotWidth / Math.max(count - 1, 1);
    var idx = Math.round((localX - layout.plotX) / step);
    return Math.max(0, Math.min(count - 1, idx));
}
/** Bar index from plot-local X (column-based charts, band scale). */
export function barIndexAtX(count, layout, localX) {
    var step = layout.plotWidth / Math.max(count, 1);
    if (step <= 0)
        return 0;
    var idx = Math.floor((localX - layout.plotX) / step);
    return Math.max(0, Math.min(count - 1, idx));
}
export function barGeometry(index, count, value, layout, bounds, gap) {
    if (gap === void 0) { gap = 0.2; }
    var step = layout.plotWidth / Math.max(count, 1);
    var bw = bandWidth(count, layout.plotWidth, gap);
    var range = bounds.max - bounds.min || 1;
    var height = ((value - bounds.min) / range) * layout.plotHeight;
    var x = layout.plotX + index * step + (step - bw) / 2;
    var y = layout.plotY + layout.plotHeight - height;
    return { x: x, y: y, width: bw, height: height, centerX: x + bw / 2 };
}
/** Row index from plot-local Y (horizontal bar charts). */
export function barIndexAtY(count, layout, localY) {
    var slot = layout.plotHeight / Math.max(count, 1);
    if (slot <= 0)
        return 0;
    var idx = Math.floor((localY - layout.plotY) / slot);
    return Math.max(0, Math.min(count - 1, idx));
}
export function stackedHorizontalBarGeometry(index, count, total, layout, bounds, gap) {
    if (gap === void 0) { gap = 0.2; }
    var slot = layout.plotHeight / Math.max(count, 1);
    var bh = bandWidth(count, layout.plotHeight, gap);
    var xScale = linearScale([bounds.min, bounds.max], [0, layout.plotWidth]);
    var x0 = layout.plotX + xScale(bounds.min);
    var x1 = layout.plotX + xScale(total);
    var y = layout.plotY + index * slot + (slot - bh) / 2;
    return { x: x0, y: y, width: Math.max(1, x1 - x0), height: bh, centerY: y + bh / 2 };
}
var CHART_TOOLTIP_PAD_X = 8;
var CHART_TOOLTIP_PAD_Y = 5;
var CHART_TOOLTIP_LINE_H = 13;
var CHART_TOOLTIP_MAX_W = 200;
export function chartTooltipSize(label) {
    var lines = label.split('\n');
    var maxLen = Math.max.apply(Math, __spreadArray(__spreadArray([], __read(lines.map(function (l) { return l.length; })), false), [1], false));
    var width = Math.min(CHART_TOOLTIP_MAX_W, Math.max(40, Math.ceil(maxLen * 6.2) + CHART_TOOLTIP_PAD_X * 2));
    var height = Math.max(22, lines.length * CHART_TOOLTIP_LINE_H + CHART_TOOLTIP_PAD_Y * 2);
    return { width: width, height: height };
}
export function chartTooltipWidth(label) {
    return chartTooltipSize(label).width;
}
/** Place tooltip box and label text inside it (works on canvas and HTML renderers). */
export function positionChartTooltip(tooltip, tooltipLabel, centerX, topY, label, chartBounds) {
    var trimmed = label.trim();
    if (!trimmed) {
        hideChartTooltip(tooltip, tooltipLabel);
        return;
    }
    var _a = chartTooltipSize(trimmed), tw = _a.width, th = _a.height;
    var x = centerX - tw / 2;
    var y = topY;
    if (chartBounds) {
        x = Math.max(4, Math.min(x, chartBounds.width - tw - 4));
        y = Math.max(4, Math.min(y, chartBounds.height - th - 4));
    }
    tooltip.x = x;
    tooltip.y = y;
    tooltip.width = tw;
    tooltip.height = th;
    tooltip.visible = true;
    tooltipLabel.text = trimmed;
    tooltipLabel.textAlign = 'center';
    tooltipLabel.x = x + tw / 2;
    var lines = trimmed.split('\n');
    var textBlockH = lines.length * CHART_TOOLTIP_LINE_H;
    tooltipLabel.y = y + Math.max(CHART_TOOLTIP_PAD_Y, (th - textBlockH) / 2);
    tooltipLabel.visible = true;
    tooltipLabel.zIndex = Math.max(tooltipLabel.zIndex, 902);
    tooltipLabel.metadata = __assign(__assign({}, tooltipLabel.metadata), { textBoxWidth: tw, chartTooltipLabel: true });
}
export function hideChartTooltip(tooltip, tooltipLabel) {
    tooltip.visible = false;
    tooltipLabel.visible = false;
}
export function wireChartInteraction(group, data, layout, bounds, parts) {
    var hoverIndex = -1;
    var updateHover = function (localX) {
        var _a;
        var idx = nearestDataIndex(data, layout, localX);
        if (idx === hoverIndex && parts.tooltip.visible)
            return;
        hoverIndex = idx;
        var pts = seriesToPoints(data, layout, bounds);
        var px = pts[idx * 2];
        var py = pts[idx * 2 + 1];
        var val = data[idx];
        var label = String(val);
        parts.crosshair.x = px;
        parts.crosshair.y = layout.plotY;
        parts.crosshair.x2 = 0;
        parts.crosshair.y2 = layout.plotHeight;
        parts.crosshair.visible = true;
        parts.dot.x = px - 4;
        parts.dot.y = py - 4;
        parts.dot.visible = true;
        positionChartTooltip(parts.tooltip, parts.tooltipLabel, px, py - 36, label, {
            width: layout.plotX + layout.plotWidth + 10,
            height: layout.plotY + layout.plotHeight + 30,
        });
        parts.crosshair.markDirty();
        parts.dot.markDirty();
        parts.tooltip.markDirty();
        parts.tooltipLabel.markDirty();
        group.markDirty();
        group.emit('hover', syntheticEvent('hover', group, { index: idx, value: val }));
        (_a = group.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
    };
    var clearHover = function () {
        var _a;
        if (hoverIndex < 0 && !parts.tooltip.visible)
            return;
        hoverIndex = -1;
        parts.crosshair.visible = false;
        parts.dot.visible = false;
        hideChartTooltip(parts.tooltip, parts.tooltipLabel);
        parts.crosshair.markDirty();
        parts.dot.markDirty();
        parts.tooltip.markDirty();
        parts.tooltipLabel.markDirty();
        group.markDirty();
        (_a = group.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
    };
    var localXFrom = function (e) {
        return chartLocalPoint(group, e.worldX, e.worldY).x;
    };
    group.on('mousemove', function (e) {
        updateHover(localXFrom(e));
    });
    group.on('mouseleave', clearHover);
    parts.hitArea.on('mousemove', function (e) {
        updateHover(localXFrom(e));
    });
    parts.hitArea.on('mouseleave', clearHover);
    group.on('click', function (e) {
        var idx = nearestDataIndex(data, layout, localXFrom(e));
        group.emit('select', syntheticEvent('select', group, { index: idx, value: data[idx] }));
    });
}
/** Crosshair hover showing all series values at the hovered index. */
export function wireMultiSeriesChartInteraction(group, seriesList, layout, bounds, parts) {
    var _a, _b;
    var primaryData = (_b = (_a = seriesList[0]) === null || _a === void 0 ? void 0 : _a.data) !== null && _b !== void 0 ? _b : [];
    var pointCount = Math.max.apply(Math, __spreadArray(__spreadArray([], __read(seriesList.map(function (s) { return s.data.length; })), false), [1], false));
    var hoverIndex = -1;
    var updateHover = function (localX) {
        var _a, _b, _c, _d, _e;
        var idx = nearestPlotIndex(pointCount, layout, localX);
        if (idx === hoverIndex && parts.tooltip.visible)
            return;
        hoverIndex = idx;
        var label = seriesList
            .map(function (s) { var _a, _b; return "".concat((_a = s.name) !== null && _a !== void 0 ? _a : 'Series', ": ").concat((_b = s.data[idx]) !== null && _b !== void 0 ? _b : '—'); })
            .join('\n');
        var anchor = primaryData.length ? primaryData : (_b = (_a = seriesList[0]) === null || _a === void 0 ? void 0 : _a.data) !== null && _b !== void 0 ? _b : [0];
        var pts = seriesToPoints(anchor, layout, bounds);
        var px = (_c = pts[idx * 2]) !== null && _c !== void 0 ? _c : layout.plotX;
        var py = (_d = pts[idx * 2 + 1]) !== null && _d !== void 0 ? _d : layout.plotY;
        parts.crosshair.x = px;
        parts.crosshair.y = layout.plotY;
        parts.crosshair.x2 = 0;
        parts.crosshair.y2 = layout.plotHeight;
        parts.crosshair.visible = true;
        parts.dot.x = px - 4;
        parts.dot.y = py - 4;
        parts.dot.visible = true;
        positionChartTooltip(parts.tooltip, parts.tooltipLabel, px, py - 36, label, {
            width: layout.plotX + layout.plotWidth + 10,
            height: layout.plotY + layout.plotHeight + 30,
        });
        parts.crosshair.markDirty();
        parts.dot.markDirty();
        parts.tooltip.markDirty();
        parts.tooltipLabel.markDirty();
        group.markDirty();
        group.emit('hover', syntheticEvent('hover', group, { index: idx, series: seriesList.map(function (s) { return s.data[idx]; }) }));
        (_e = group.getApp()) === null || _e === void 0 ? void 0 : _e.requestRender();
    };
    var clearHover = function () {
        var _a;
        if (hoverIndex < 0 && !parts.tooltip.visible)
            return;
        hoverIndex = -1;
        parts.crosshair.visible = false;
        parts.dot.visible = false;
        hideChartTooltip(parts.tooltip, parts.tooltipLabel);
        parts.crosshair.markDirty();
        parts.dot.markDirty();
        parts.tooltip.markDirty();
        parts.tooltipLabel.markDirty();
        group.markDirty();
        (_a = group.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
    };
    var localXFrom = function (e) {
        return chartLocalPoint(group, e.worldX, e.worldY).x;
    };
    group.on('mousemove', function (e) {
        updateHover(localXFrom(e));
    });
    group.on('mouseleave', clearHover);
    parts.hitArea.on('mousemove', function (e) {
        updateHover(localXFrom(e));
    });
    parts.hitArea.on('mouseleave', clearHover);
    group.on('click', function (e) {
        var idx = nearestPlotIndex(pointCount, layout, localXFrom(e));
        group.emit('select', syntheticEvent('select', group, { index: idx, series: seriesList.map(function (s) { return s.data[idx]; }) }));
    });
}
export function wireBarChartInteraction(group, data, layout, bounds, gap, parts) {
    if (gap === void 0) { gap = 0.2; }
    var count = data.length;
    var hoverIndex = -1;
    var updateHover = function (localX) {
        var _a;
        var idx = barIndexAtX(count, layout, localX);
        if (idx === hoverIndex && parts.tooltip.visible)
            return;
        hoverIndex = idx;
        var val = data[idx];
        var geo = barGeometry(idx, count, val, layout, bounds, gap);
        var label = String(val);
        parts.highlight.x = geo.x;
        parts.highlight.y = geo.y;
        var hi = parts.highlight;
        hi.width = geo.width;
        hi.height = geo.height;
        parts.highlight.visible = true;
        positionChartTooltip(parts.tooltip, parts.tooltipLabel, geo.centerX, geo.y - 32, label, {
            width: layout.plotX + layout.plotWidth + 10,
            height: layout.plotY + layout.plotHeight + 30,
        });
        parts.highlight.markDirty();
        parts.tooltip.markDirty();
        parts.tooltipLabel.markDirty();
        group.markDirty();
        group.emit('hover', syntheticEvent('hover', group, { index: idx, value: val }));
        (_a = group.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
    };
    var clearHover = function () {
        var _a;
        if (hoverIndex < 0 && !parts.tooltip.visible)
            return;
        hoverIndex = -1;
        parts.highlight.visible = false;
        hideChartTooltip(parts.tooltip, parts.tooltipLabel);
        parts.highlight.markDirty();
        parts.tooltip.markDirty();
        parts.tooltipLabel.markDirty();
        group.markDirty();
        (_a = group.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
    };
    var localXFrom = function (e) {
        return chartLocalPoint(group, e.worldX, e.worldY).x;
    };
    group.on('mousemove', function (e) {
        updateHover(localXFrom(e));
    });
    group.on('mouseleave', clearHover);
    parts.hitArea.on('mousemove', function (e) {
        updateHover(localXFrom(e));
    });
    parts.hitArea.on('mouseleave', clearHover);
    group.on('click', function (e) {
        var idx = barIndexAtX(count, layout, localXFrom(e));
        group.emit('select', syntheticEvent('select', group, { index: idx, value: data[idx] }));
    });
}
export function stackedBarHoverLabel(seriesList, index) {
    return seriesList.map(function (s) { var _a, _b; return "".concat((_a = s.name) !== null && _a !== void 0 ? _a : 'Series', ": ").concat((_b = s.data[index]) !== null && _b !== void 0 ? _b : '—'); }).join('\n');
}
/** Column hover for stacked bar charts — lists each series segment at the hovered index. */
export function wireStackedBarChartInteraction(group, seriesList, layout, bounds, totals, gap, parts) {
    if (gap === void 0) { gap = 0.2; }
    var count = totals.length;
    var hoverIndex = -1;
    var updateHover = function (localX) {
        var _a, _b;
        var idx = barIndexAtX(count, layout, localX);
        if (idx === hoverIndex && parts.tooltip.visible)
            return;
        hoverIndex = idx;
        var total = (_a = totals[idx]) !== null && _a !== void 0 ? _a : 0;
        var geo = barGeometry(idx, count, total, layout, bounds, gap);
        var label = stackedBarHoverLabel(seriesList, idx);
        parts.highlight.x = geo.x;
        parts.highlight.y = geo.y;
        var hi = parts.highlight;
        hi.width = geo.width;
        hi.height = geo.height;
        parts.highlight.visible = true;
        positionChartTooltip(parts.tooltip, parts.tooltipLabel, geo.centerX, geo.y - 32, label, {
            width: layout.plotX + layout.plotWidth + 10,
            height: layout.plotY + layout.plotHeight + 30,
        });
        parts.highlight.markDirty();
        parts.tooltip.markDirty();
        parts.tooltipLabel.markDirty();
        group.markDirty();
        group.emit('hover', syntheticEvent('hover', group, {
            index: idx,
            series: seriesList.map(function (s) { return s.data[idx]; }),
        }));
        (_b = group.getApp()) === null || _b === void 0 ? void 0 : _b.requestRender();
    };
    var clearHover = function () {
        var _a;
        if (hoverIndex < 0 && !parts.tooltip.visible)
            return;
        hoverIndex = -1;
        parts.highlight.visible = false;
        hideChartTooltip(parts.tooltip, parts.tooltipLabel);
        parts.highlight.markDirty();
        parts.tooltip.markDirty();
        parts.tooltipLabel.markDirty();
        group.markDirty();
        (_a = group.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
    };
    var localXFrom = function (e) {
        return chartLocalPoint(group, e.worldX, e.worldY).x;
    };
    group.on('mousemove', function (e) {
        updateHover(localXFrom(e));
    });
    group.on('mouseleave', clearHover);
    parts.hitArea.on('mousemove', function (e) {
        updateHover(localXFrom(e));
    });
    parts.hitArea.on('mouseleave', clearHover);
    group.on('click', function (e) {
        var idx = barIndexAtX(count, layout, localXFrom(e));
        group.emit('select', syntheticEvent('select', group, {
            index: idx,
            series: seriesList.map(function (s) { return s.data[idx]; }),
        }));
    });
}
/** Row hover for stacked horizontal bar charts — lists each series segment at the hovered index. */
export function wireStackedHorizontalBarChartInteraction(group, seriesList, layout, bounds, totals, gap, parts) {
    if (gap === void 0) { gap = 0.2; }
    var count = totals.length;
    var hoverIndex = -1;
    var updateHover = function (localY) {
        var _a, _b;
        var idx = barIndexAtY(count, layout, localY);
        if (idx === hoverIndex && parts.tooltip.visible)
            return;
        hoverIndex = idx;
        var total = (_a = totals[idx]) !== null && _a !== void 0 ? _a : 0;
        var geo = stackedHorizontalBarGeometry(idx, count, total, layout, bounds, gap);
        var label = stackedBarHoverLabel(seriesList, idx);
        parts.highlight.x = geo.x;
        parts.highlight.y = geo.y;
        var hi = parts.highlight;
        hi.width = geo.width;
        hi.height = geo.height;
        parts.highlight.visible = true;
        positionChartTooltip(parts.tooltip, parts.tooltipLabel, layout.plotX + layout.plotWidth / 2, geo.y - 32, label, {
            width: layout.plotX + layout.plotWidth + 10,
            height: layout.plotY + layout.plotHeight + 30,
        });
        parts.highlight.markDirty();
        parts.tooltip.markDirty();
        parts.tooltipLabel.markDirty();
        group.markDirty();
        group.emit('hover', syntheticEvent('hover', group, {
            index: idx,
            series: seriesList.map(function (s) { return s.data[idx]; }),
        }));
        (_b = group.getApp()) === null || _b === void 0 ? void 0 : _b.requestRender();
    };
    var clearHover = function () {
        var _a;
        if (hoverIndex < 0 && !parts.tooltip.visible)
            return;
        hoverIndex = -1;
        parts.highlight.visible = false;
        hideChartTooltip(parts.tooltip, parts.tooltipLabel);
        parts.highlight.markDirty();
        parts.tooltip.markDirty();
        parts.tooltipLabel.markDirty();
        group.markDirty();
        (_a = group.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
    };
    var localYFrom = function (e) {
        return chartLocalPoint(group, e.worldX, e.worldY).y;
    };
    group.on('mousemove', function (e) {
        updateHover(localYFrom(e));
    });
    group.on('mouseleave', clearHover);
    parts.hitArea.on('mousemove', function (e) {
        updateHover(localYFrom(e));
    });
    parts.hitArea.on('mouseleave', clearHover);
    group.on('click', function (e) {
        var idx = barIndexAtY(count, layout, localYFrom(e));
        group.emit('select', syntheticEvent('select', group, {
            index: idx,
            series: seriesList.map(function (s) { return s.data[idx]; }),
        }));
    });
}
